import { NbxCapacitorFirebaseAuth } from '@nbx/capacitor-firebase-auth';

import { getNativeUser, requireUser } from './helpers';

import type { FirebaseContext } from './context';
import type { FirebaseAuthModule, FirebaseMfaMethods } from './types';

interface FirebaseMfaDeps {
  importAuth: () => Promise<FirebaseAuthModule>;
  user: () => Promise<import('firebase/auth').User | null>;
  isNativeAuth: () => boolean;
}

export function createFirebaseMfaMethods(
  context: FirebaseContext,
  deps: FirebaseMfaDeps
): FirebaseMfaMethods {
  const { log } = context;

  const multiFactorAssertion: FirebaseMfaMethods['multiFactorAssertion'] = async ({
    verificationId,
    verificationCode
  }) => {
    const firebaseAuth = await deps.importAuth();
    const credential = firebaseAuth.PhoneAuthProvider.credential(verificationId, verificationCode);
    log.info('multiFactorAssertion, verify sms', !!credential);
    return firebaseAuth.PhoneMultiFactorGenerator.assertion(credential);
  };

  return {
    multiFactorUser: async () => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          try {
            return await NbxCapacitorFirebaseAuth.getMultiFactorUser();
          } catch (error) {
            console.warn(
              'firebase.service, cannot get NbxCapacitorFirebaseAuth.getMultiFactorUser',
              (error as Error).message
            );
            return null;
          }
        }
      }

      const firebaseAuth = await deps.importAuth();

      try {
        return await firebaseAuth.multiFactor(await requireUser(deps.user, 'get multi-factor user'));
      } catch (error) {
        console.warn('firebase.service, cannot get firebaseAuth.multiFactor', (error as Error).message);
        return null;
      }
    },

    getMultiFactorResolver: async error => {
      const firebaseAuth = await deps.importAuth();
      return firebaseAuth.getMultiFactorResolver(await firebaseAuth.getAuth(), error);
    },

    verifyPhoneNumber: async ({ phoneInfoOptions, recaptchaVerifier }) => {
      const firebaseAuth = await deps.importAuth();
      const auth = await firebaseAuth.getAuth();

      log.info(
        'verifyPhoneNumber, create PhoneAuthProvider, phoneInfoOptions = ',
        !!phoneInfoOptions,
        'recaptchaVerifier = ',
        !!recaptchaVerifier
      );

      const phoneAuthProvider = new firebaseAuth.PhoneAuthProvider(auth);
      log.info(
        'verifyPhoneNumber, preparing sending sms, renderring recaptchaVerifier, phoneAuthProvider = ',
        !!phoneAuthProvider
      );

      return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
    },

    multiFactorAssertion,

    enrollMultiFactorUser: async ({ verificationId, verificationCode }) => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native NbxCapacitorFirebaseAuth.enrollMultiFactorUser()');

          try {
            const result = await NbxCapacitorFirebaseAuth.enrollMultiFactorUser({
              verificationId,
              verificationCode
            });
            log.info('NbxCapacitorFirebaseAuth.enrollMultiFactorUser done');
            return result;
          } catch (error) {
            console.warn(
              'firebase.service, cannot get NbxCapacitorFirebaseAuth.enrollMultiFactorUser',
              (error as Error).message
            );
            return null;
          }
        }
      }

      const firebaseAuth = await deps.importAuth();
      log.info('enrollMultiFactorUser, multiFactorAssertion');
      const assertion = await multiFactorAssertion({ verificationId, verificationCode });
      log.info('enrollMultiFactorUser, enroll user', !!assertion);
      return firebaseAuth
        .multiFactor(await requireUser(deps.user, 'enroll multi-factor user'))
        .enroll(assertion);
    },

    unenrollMultiFactorUser: async ({ factorUid }) => {
      if (deps.isNativeAuth()) {
        const nativeUser = await getNativeUser();
        if (nativeUser) {
          log.info('Using native NbxCapacitorFirebaseAuth.unenrollMultiFactorUser()');
          return NbxCapacitorFirebaseAuth.unenrollMultiFactorUser({ factorUid });
        }
      }

      const firebaseAuth = await deps.importAuth();
      return firebaseAuth
        .multiFactor(await requireUser(deps.user, 'unenroll multi-factor user'))
        .unenroll(factorUid);
    }
  };
}
